/**
 * Client-side VulForm handler for Astro components
 * Provides progressive enhancement for server-rendered forms
 */

import {
  VulFormApiClient,
  VulFormError,
  FormTemplate,
  ValidationErrors,
  SubmissionResponse,
  FormValidator,
} from '@vulform/core';

interface VulFormClientConfig {
  templateId: string;
  apiKey?: string;
  apiUrl?: string;
  theme?: string;
  prefill?: Record<string, any>;
  enableAnalytics?: boolean;
  spamProtection?: boolean;
  loadingState?: string;
  resetOnSuccess?: boolean;
  showSuccessMessage?: boolean;
  autoRedirect?: boolean;
}

export class VulFormClient {
  private element: HTMLElement;
  private config: VulFormClientConfig;
  private apiClient: VulFormApiClient | null = null;
  private template: FormTemplate | null = null;
  private formData: Record<string, any> = {};
  private validationErrors: ValidationErrors = {};
  private isSubmitting = false;
  private isSubmitted = false;

  constructor(element: HTMLElement, config: VulFormClientConfig) {
    this.element = element;
    this.config = config;
    this.formData = { ...config.prefill };

    this.initialize();
  }

  private async initialize() {
    try {
      await this.initializeApiClient();
      await this.loadTemplate();
      this.setupForm();
    } catch (error) {
      this.showError(
        error instanceof VulFormError
          ? error
          : new VulFormError('INIT_ERROR', 'Failed to initialize form')
      );
    }
  }

  private async initializeApiClient() {
    const apiKey = this.config.apiKey || this.getApiKeyFromEnv();

    if (!apiKey) {
      throw new VulFormError('MISSING_API_KEY', 'API key is required');
    }

    this.apiClient = new VulFormApiClient({
      apiKey,
      baseUrl: this.config.apiUrl,
      debug: window.location.hostname === 'localhost',
    });
  }

  private getApiKeyFromEnv(): string | undefined {
    // Check for Astro environment variables
    return (window as any).__VULFORM_API_KEY__ || (window as any).VULFORM_API_KEY || undefined;
  }

  private async loadTemplate() {
    if (!this.apiClient) return;

    const templateData = await this.apiClient.getTemplate(this.config.templateId);
    this.template = templateData;

    // Set default values
    const defaults: Record<string, any> = {};
    templateData.fields.forEach(field => {
      if (field.defaultValue !== undefined) {
        defaults[field.name] = field.defaultValue;
      }
    });

    this.formData = { ...defaults, ...this.formData };

    // Track form view
    if (this.config.enableAnalytics && this.apiClient) {
      this.apiClient.trackEvent({
        type: 'form_view',
        templateId: this.config.templateId,
        timestamp: Date.now(),
      });
    }
  }

  private setupForm() {
    if (!this.template) return;

    this.hideLoading();
    this.renderForm();
    this.attachEventListeners();
  }

  private hideLoading() {
    const loadingElement = this.element.querySelector('[data-loading="true"]');
    if (loadingElement) {
      (loadingElement as HTMLElement).style.display = 'none';
    }
  }

  private renderForm() {
    if (!this.template) return;

    const formContainer = this.element.querySelector('[data-form="true"]') as HTMLElement;
    if (!formContainer) return;

    formContainer.innerHTML = this.generateFormHTML();
    formContainer.style.display = 'block';
    formContainer.classList.remove('hidden');
  }

  private generateFormHTML(): string {
    if (!this.template) return '';

    const { fields, theme, settings } = this.template;
    const layoutClasses = this.getLayoutClasses();

    return `
      <form class="vulform-form" data-vulform-form>
        ${
          this.template.name
            ? `
          <div class="vulform-header mb-6">
            <h2 class="text-2xl font-semibold text-gray-900 mb-2">${this.template.name}</h2>
            ${this.template.description ? `<p class="text-gray-600">${this.template.description}</p>` : ''}
          </div>
        `
            : ''
        }
        
        <div class="${layoutClasses}">
          ${fields.map(field => this.generateFieldHTML(field)).join('')}
        </div>
        
        <div class="vulform-submit-section mt-8 pt-6 border-t border-gray-200">
          <button
            type="submit"
            class="vulform-submit-button w-full px-6 py-3 text-white font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
            style="${theme?.primaryColor ? `background-color: ${theme.primaryColor}` : ''}"
          >
            ${settings?.submitText || 'Submit'}
          </button>
        </div>
        
        <div class="vulform-form-errors mt-4 hidden">
          <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-red-800 text-sm vulform-error-message"></p>
          </div>
        </div>
      </form>
    `;
  }

  private generateFieldHTML(field: any): string {
    const fieldId = `field-${field.id}`;
    const fieldClasses = this.getFieldClasses(field);
    const fieldWidth = this.getFieldWidth(field.width);

    return `
      <div class="${fieldWidth}">
        <div class="vulform-field">
          ${
            field.label
              ? `
            <label for="${fieldId}" class="vulform-label block text-sm font-medium text-gray-700 mb-2">
              ${field.label}
              ${field.required ? '<span class="text-red-500 ml-1">*</span>' : ''}
            </label>
          `
              : ''
          }
          
          ${
            field.description
              ? `
            <p class="text-sm text-gray-600 mb-2">${field.description}</p>
          `
              : ''
          }
          
          ${this.generateFieldInput(field, fieldId, fieldClasses)}
          
          <div class="vulform-field-error mt-1 text-sm text-red-600 hidden" data-field-error="${field.name}"></div>
          
          ${
            field.helpText
              ? `
            <p class="mt-1 text-sm text-gray-500">${field.helpText}</p>
          `
              : ''
          }
        </div>
      </div>
    `;
  }

  private generateFieldInput(field: any, fieldId: string, fieldClasses: string): string {
    const commonAttrs = `
      id="${fieldId}"
      name="${field.name}"
      class="${fieldClasses}"
      ${field.required ? 'required' : ''}
      ${field.placeholder ? `placeholder="${field.placeholder}"` : ''}
      data-field-name="${field.name}"
    `;

    switch (field.type) {
      case 'textarea':
        return `<textarea ${commonAttrs} rows="${field.options?.rows || 4}">${this.formData[field.name] || ''}</textarea>`;

      case 'select':
        return `
          <select ${commonAttrs}>
            ${!field.required ? '<option value="">Select an option</option>' : ''}
            ${
              field.options?.choices
                ?.map(
                  (choice: any) =>
                    `<option value="${choice.value}" ${this.formData[field.name] === choice.value ? 'selected' : ''}>${choice.label}</option>`
                )
                .join('') || ''
            }
          </select>
        `;

      case 'radio':
        return `
          <div class="space-y-2">
            ${
              field.options?.choices
                ?.map(
                  (choice: any) => `
              <div class="flex items-center">
                <input
                  type="radio"
                  id="${fieldId}-${choice.value}"
                  name="${field.name}"
                  value="${choice.value}"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  ${this.formData[field.name] === choice.value ? 'checked' : ''}
                />
                <label for="${fieldId}-${choice.value}" class="ml-3 block text-sm font-medium text-gray-700">
                  ${choice.label}
                </label>
              </div>
            `
                )
                .join('') || ''
            }
          </div>
        `;

      case 'checkbox':
        return `
          <div class="space-y-2">
            ${
              field.options?.choices
                ?.map((choice: any) => {
                  const isChecked = Array.isArray(this.formData[field.name])
                    ? this.formData[field.name].includes(choice.value)
                    : this.formData[field.name] === choice.value;
                  return `
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    id="${fieldId}-${choice.value}"
                    name="${field.name}"
                    value="${choice.value}"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    ${isChecked ? 'checked' : ''}
                  />
                  <label for="${fieldId}-${choice.value}" class="ml-3 block text-sm font-medium text-gray-700">
                    ${choice.label}
                  </label>
                </div>
              `;
                })
                .join('') || ''
            }
          </div>
        `;

      default:
        const inputType = this.getInputType(field.type);
        return `<input ${commonAttrs} type="${inputType}" value="${this.formData[field.name] || ''}" />`;
    }
  }

  private getInputType(fieldType: string): string {
    switch (fieldType) {
      case 'email':
        return 'email';
      case 'phone':
        return 'tel';
      case 'number':
        return 'number';
      case 'url':
        return 'url';
      case 'date':
        return 'date';
      case 'time':
        return 'time';
      case 'datetime':
        return 'datetime-local';
      default:
        return 'text';
    }
  }

  private getLayoutClasses(): string {
    if (!this.template) return 'space-y-6';

    const hasCustomWidths = this.template.fields.some(
      field => field.width && field.width !== 'full'
    );

    switch (this.template.layout) {
      case 'horizontal':
        return 'grid grid-cols-1 md:grid-cols-2 gap-6';
      case 'grid':
        return 'grid grid-cols-12 gap-6';
      default:
        return hasCustomWidths ? 'grid grid-cols-12 gap-6' : 'space-y-6';
    }
  }

  private getFieldWidth(width?: string): string {
    switch (width) {
      case 'full':
        return 'col-span-full';
      case 'half':
        return 'col-span-full md:col-span-6';
      case 'third':
        return 'col-span-full md:col-span-4';
      case 'quarter':
        return 'col-span-full md:col-span-3';
      default:
        return 'col-span-full';
    }
  }

  private getFieldClasses(field: any): string {
    const baseClasses =
      'block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm';
    return baseClasses;
  }

  private attachEventListeners() {
    const form = this.element.querySelector('[data-vulform-form]') as HTMLFormElement;
    if (!form) return;

    // Handle form submission
    form.addEventListener('submit', this.handleSubmit.bind(this));

    // Handle field changes
    const fields = form.querySelectorAll('input, textarea, select');
    fields.forEach(field => {
      field.addEventListener('change', this.handleFieldChange.bind(this));
      field.addEventListener('blur', this.handleFieldBlur.bind(this));
    });
  }

  private handleFieldChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const fieldName = target.getAttribute('data-field-name');
    if (!fieldName) return;

    if (target.type === 'checkbox') {
      this.handleCheckboxChange(target, fieldName);
    } else {
      this.formData[fieldName] = target.value;
    }

    // Clear field error
    this.clearFieldError(fieldName);
  }

  private handleCheckboxChange(checkbox: HTMLInputElement, fieldName: string) {
    const existingValue = this.formData[fieldName];
    const value = checkbox.value;

    if (Array.isArray(existingValue)) {
      if (checkbox.checked && !existingValue.includes(value)) {
        this.formData[fieldName] = [...existingValue, value];
      } else if (!checkbox.checked) {
        this.formData[fieldName] = existingValue.filter(v => v !== value);
      }
    } else {
      this.formData[fieldName] = checkbox.checked ? value : '';
    }
  }

  private handleFieldBlur(event: Event) {
    const target = event.target as HTMLInputElement;
    const fieldName = target.getAttribute('data-field-name');
    if (!fieldName || !this.template) return;

    // Validate individual field
    const field = this.template.fields.find(f => f.name === fieldName);
    if (field) {
      const errors = FormValidator.validateField(this.formData[fieldName], field);
      if (errors && errors.length > 0) {
        this.showFieldError(fieldName, errors[0]);
      }
    }
  }

  private async handleSubmit(event: Event) {
    event.preventDefault();

    if (this.isSubmitting || !this.apiClient || !this.template) return;

    this.isSubmitting = true;
    this.updateSubmitButton(true);
    this.clearErrors();

    try {
      // Validate form
      const errors = FormValidator.validateForm(this.formData, this.template);
      if (Object.keys(errors).length > 0) {
        this.showValidationErrors(errors);
        return;
      }

      // Submit form
      const response = await this.apiClient.submitForm({
        templateId: this.config.templateId,
        data: this.formData,
      });

      this.isSubmitted = true;
      this.showSuccess(response);

      if (this.config.resetOnSuccess) {
        this.resetForm();
      }
    } catch (error) {
      this.showError(
        error instanceof VulFormError
          ? error
          : new VulFormError('SUBMIT_ERROR', 'Form submission failed')
      );
    } finally {
      this.isSubmitting = false;
      this.updateSubmitButton(false);
    }
  }

  private showValidationErrors(errors: ValidationErrors) {
    Object.entries(errors).forEach(([fieldName, message]) => {
      this.showFieldError(fieldName, message);
    });
  }

  private showFieldError(fieldName: string, message: string) {
    const errorElement = this.element.querySelector(
      `[data-field-error="${fieldName}"]`
    ) as HTMLElement;
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.remove('hidden');
    }
  }

  private clearFieldError(fieldName: string) {
    const errorElement = this.element.querySelector(
      `[data-field-error="${fieldName}"]`
    ) as HTMLElement;
    if (errorElement) {
      errorElement.classList.add('hidden');
    }
  }

  private clearErrors() {
    const errorElements = this.element.querySelectorAll('[data-field-error]');
    errorElements.forEach(element => {
      (element as HTMLElement).classList.add('hidden');
    });

    const formErrors = this.element.querySelector('.vulform-form-errors') as HTMLElement;
    if (formErrors) {
      formErrors.classList.add('hidden');
    }
  }

  private showError(error: VulFormError) {
    const errorContainer = this.element.querySelector('.vulform-error') as HTMLElement;
    const errorMessage = this.element.querySelector('.vulform-error-message') as HTMLElement;

    if (errorContainer && errorMessage) {
      errorMessage.textContent = error.message;
      errorContainer.style.display = 'block';
      errorContainer.classList.remove('hidden');
    }

    // Hide loading
    this.hideLoading();
  }

  private showSuccess(response: SubmissionResponse) {
    const successContainer = this.element.querySelector('.vulform-success') as HTMLElement;
    const successMessage = this.element.querySelector('.vulform-success-message') as HTMLElement;
    const formContainer = this.element.querySelector('[data-form="true"]') as HTMLElement;

    if (successContainer && formContainer) {
      if (successMessage && this.template?.settings?.successMessage) {
        successMessage.textContent = this.template.settings.successMessage;
      }

      formContainer.style.display = 'none';
      successContainer.style.display = 'block';
      successContainer.classList.remove('hidden');
    }
  }

  private updateSubmitButton(isSubmitting: boolean) {
    const submitButton = this.element.querySelector('.vulform-submit-button') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = isSubmitting;
      submitButton.textContent = isSubmitting
        ? 'Sending...'
        : this.template?.settings?.submitText || 'Submit';
    }
  }

  private resetForm() {
    this.formData = { ...this.config.prefill };
    this.isSubmitted = false;

    const formContainer = this.element.querySelector('[data-form="true"]') as HTMLElement;
    const successContainer = this.element.querySelector('.vulform-success') as HTMLElement;

    if (formContainer && successContainer) {
      successContainer.style.display = 'none';
      formContainer.style.display = 'block';

      // Reset form fields
      const form = formContainer.querySelector('form') as HTMLFormElement;
      if (form) {
        form.reset();
      }
    }
  }
}
